/**
 * POS payment methods — admin-managed registry for the Cashier checkout,
 * mirroring the `ecom-payment-methods` / `payout-deductions` pattern (config in
 * `tenant_config.features.pos_payment_methods.methods[]`, no dedicated table,
 * typed admin editor — no raw JSON).
 *
 * Unlike ecom (which models online/member settlement: wallet/manual/gateway/cod),
 * an in-person POS tender is captured by `tenderKind`:
 *
 *  - `cash`   → cash-with-change. The cashier enters the amount tendered; the
 *               drawer gives change (amountTendered may exceed the total).
 *  - `exact`  → an exact non-cash tender with no change (e.g. a card swipe). The
 *               tendered amount must equal the order total. (Legacy `'card'`.)
 *  - `wallet` → debit the buyer slot's wallet in `currency` via
 *               `spendWalletAtCheckout`; no change. Requires an active buyer slot.
 *               `gc`/`coin` debit the per-currency order total (flat per-currency
 *               price on every line). `cash` debits the CASH wallet for the
 *               NORMAL cash total — discounts, shipping, PV, and commissions all
 *               apply exactly like a drawer-cash sale; the member just pays from
 *               their cash balance instead of physical tender.
 *
 * The chosen method's `code` + `label` are recorded on `pos_payments`
 * (`method_code` / `method_label`) for the daily-report breakdown; the coarse
 * `pos_payments.method` enum keeps storing the tender bucket for back-compat.
 */

export type PosPaymentTenderKind = 'cash' | 'exact' | 'wallet';
/** Which slot-wallet column a `wallet` method debits. Ignored for cash/exact. */
export type PosPaymentCurrency = 'cash' | 'gc' | 'coin';

export type PosPaymentMethod = {
  /** Stable slug recorded on `pos_payments.method_code`. */
  code: string;
  /** User-facing name shown in the cashier tender picker (e.g. "GCash"). */
  label: string;
  /** How the tender is captured — see the module doc. */
  tenderKind: PosPaymentTenderKind;
  /**
   * For `tenderKind='wallet'`: which wallet currency to debit. For cash/exact
   * this is always 'cash' (the order is cash-denominated).
   */
  currency: PosPaymentCurrency;
  enabled: boolean;
};

export type PosPaymentMethodsConfig = {
  methods: PosPaymentMethod[];
};

/**
 * Built-in default methods = the previously-hardcoded cashier tender list
 * expressed as data, so every tenant has a working POS out of the box and an
 * admin can rename / disable / reorder / extend them. Wholesale-replaced once
 * the admin saves their own list.
 */
export const POS_PAYMENT_METHODS_DEFAULT: PosPaymentMethodsConfig = {
  methods: [
    { code: 'cash', label: 'Cash', tenderKind: 'cash', currency: 'cash', enabled: true },
    { code: 'card', label: 'Card', tenderKind: 'exact', currency: 'cash', enabled: true },
    { code: 'gc', label: 'GC', tenderKind: 'wallet', currency: 'gc', enabled: true },
    { code: 'coin', label: 'Coin', tenderKind: 'wallet', currency: 'coin', enabled: true },
  ],
};

/**
 * Resolve a method by code from the registry. Returns `null` when the code is
 * absent or the method is disabled (callers should reject `unknown_payment_method`
 * either way — a disabled method must not silently fall back).
 */
export function resolvePosPaymentMethod(
  cfg: PosPaymentMethodsConfig | null | undefined,
  code: string,
): PosPaymentMethod | null {
  if (!cfg) return null;
  const methods = Array.isArray(cfg.methods) ? cfg.methods : [];
  const found = methods.find((m) => m.code === code);
  if (!found || !found.enabled) return null;
  return found;
}

/** Build a blank, enabled cash method for the admin editor's "add row". */
export function makePosPaymentMethod(code = '', label = ''): PosPaymentMethod {
  return { code, label, tenderKind: 'cash', currency: 'cash', enabled: true };
}
